import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Button,
  Spin,
  Row,
  Col,
  Space,
  Card,
  Collapse,
  Steps,
  Carousel,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import Cookies from "js-cookie";
import { sha256 } from "hash.js";
import axios from "axios";
import { firebaseAPI } from "../firebaseConfig";
import InternshipGalleryCarousel from "../components/GalleryCarousel";
import axiosInstance from "../utils/axiosInstance";

const heroImages = [
  "/S24_P1.JPG",
  "/S24_P2.JPG",
  "/S24_P3.JPG",
  "/S24_P4.JPG",
  "/S24_P5.JPG",
  "/S24_Cover.jpg",
];

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;
const { Step } = Steps;

// Utility to hash a role string
const defaultHash = (input) => sha256().update(input).digest("hex");

const HomePage = ({ isLoggedIn }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathKey = decodeURI(location.pathname.split("/")[1] || "");
  const [heroIndex, setHeroIndex] = useState(5);

  const [programData, setProgramData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for judge role
  const isJudge = Cookies.get("role") === defaultHash("judge");
  const isParticipant = Cookies.get("role") === defaultHash("participant");
  const isAdmin = Cookies.get("role") === defaultHash("admin");
  // Fetch program details
  useEffect(() => {
    if (!pathKey) return;
    axiosInstance
      .get(`${firebaseAPI}/program/${pathKey}`)
      .then(({ data }) => setProgramData(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [pathKey]);

  // Rotate hero image every 3s
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setHeroIndex(prev => (prev + 1) % heroImages.length);
  //   }, 3000);
  //   return () => clearInterval(interval);
  // }, []);

  // Redirect logged-in users
  useEffect(() => {
    if (isLoggedIn && programData?.programName) {
      if (isParticipant)
        navigate(`/${programData.programName}/participantprofile`);
      if (isAdmin) navigate(`/${programData.programName}/overview`);
      if (isJudge) navigate(`/${programData.programName}/judgehome`);
    }
  }, [isLoggedIn, programData, navigate]);

  if (loading) {
    return (
      <Spin
        tip="Loading homepage..."
        style={{ width: "100%", marginTop: 100 }}
      />
    );
  }

  return (
    <div style={{ padding: 32, maxWidth: 1200, margin: "0 auto" }}>
      {isJudge && (
        <div style={{ textAlign: "right", marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={() => navigate(`/${programData.programName}/judgehome`)}
          >
            Go To Evaluations
          </Button>
        </div>
      )}

      <div
        style={{
          position: "relative",
          marginBottom: 32,
          borderRadius: 8,
          overflow: "hidden",
          height: 400,
        }}
      >
        {/* Blurred Background Layer */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${heroImages[heroIndex]})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(8px) brightness(0.5)", // <- blur + dim the image
            transform: "scale(1.05)", // <- avoid cropping edges due to blur
            transition: "background-image 1s ease-in-out",
            zIndex: 1,
          }}
        />

        {/* Foreground Content */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 24px",
            zIndex: 2,
          }}
        >
          <Title
            level={1}
            style={{ color: "#fff", textShadow: "2px 2px 8px rgba(0,0,0,0.7)" }}
          >
            {programData.programName}
          </Title>
          <Paragraph
            style={{
              fontSize: 20,
              color: "#fff",
              textShadow: "1px 1px 6px rgba(0,0,0,0.6)",
              maxWidth: 800,
              textAlign: "center",
            }}
          >
            {/* {programData.marketingParagraph} */}
          </Paragraph>
          <Button
            type="primary"
            size="large"
            disabled
            style={{
              marginTop: 16,
              backgroundColor: "#d9d9d9", // gray out
              borderColor: "#d9d9d9",
              color: "#8c8c8c",
              fontWeight: "bold",
              cursor: "not-allowed",
            }}
          >
            Applications Closed
          </Button>
        </div>
      </div>

      {/* Profile & Timeline */}
      <Row gutter={[32, 32]} style={{ marginBottom: 32 }}>
        <Col xs={24} md={12}>
          <Card bordered={false} style={{ background: "#fafafa" }}>
            <Title level={4} style={{ color: "#002766" }}>
              Profile Needed
            </Title>
            {programData.profileNeeded.map((req, i) => (
              <Paragraph key={i}>
                <CheckCircleOutlined
                  style={{ color: "#52c41a", marginRight: 8 }}
                />
                {req}
              </Paragraph>
            ))}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card bordered={false} style={{ background: "#fafafa" }}>
            <Title level={4} style={{ color: "#002766" }}>
              Application Process
            </Title>
            <Steps direction="vertical" current={-1} size="small">
              {programData.programTimeline.map((item, index) => (
                <Step
                  key={index}
                  title={item.phase}
                  icon={<ClockCircleOutlined />}
                />
              ))}
            </Steps>
          </Card>
        </Col>
      </Row>

      {/* FAQ Section */}
      <Card bordered={false} style={{ background: "#f0f5ff", padding: 24 }}>
        <Title level={4} style={{ color: "#002766" }}>
          Frequently Asked Questions
        </Title>
        <Collapse
          accordion
          expandIcon={({ isActive }) => (
            <PlusOutlined
              rotate={isActive ? 45 : 0}
              style={{ color: "#1890ff" }}
            />
          )}
        >
          {programData.FAQ.map((faq, i) => (
            <Panel header={faq.question} key={i}>
              <Paragraph>{faq.answer}</Paragraph>
            </Panel>
          ))}
        </Collapse>
      </Card>
    </div>
  );
};

export default HomePage;
